import TreeIcon from "@mui/icons-material/AccountTree";
import TimelineIcon from "@mui/icons-material/CalendarMonth";
import OutcomesIcon from "@mui/icons-material/ContentCopy";
import ClaimsIcon from "@mui/icons-material/Forum";
import AgreementsIcon from "@mui/icons-material/Grading";
import SettlementIcon from "@mui/icons-material/Handshake";
import NetResultIcon from "@mui/icons-material/Iso";
import BargainingIcon from "@mui/icons-material/LinearScale";
import ValueIcon from "@mui/icons-material/Money";
import RevenueIcon from "@mui/icons-material/Moving";
import PartiesIcon from "@mui/icons-material/People";
import FeeStructureIcon from "@mui/icons-material/PieChart";
import RecoveriesIcon from "@mui/icons-material/Replay";
import ReserveIcon from "@mui/icons-material/Savings";
import CostsIcon from "@mui/icons-material/StackedBarChart";
import OverviewIcon from "@mui/icons-material/Summarize";
import ApportionmentsIcon from "@mui/icons-material/Tune";
import RiskIcon from "@mui/icons-material/Warning";
import {
  Badge,
  Box,
  Divider,
  Drawer,
  ListItemButton,
  Toolbar,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { useCase } from "../context/CaseContext.tsx";
import { useFeatureFlags } from "../context/FeatureFlagsContext.tsx";
import { useTheme } from "../context/ThemeContext.tsx";
import { usePersona } from "../context/usePersona.ts";
import {
  feeStructurePrerequisite,
  partiesWithoutCosts,
  PrerequisiteId,
} from "../shared/casePrerequisites.ts";
import { NAVIGATION_WIDTH, RAIL_WIDTH } from "./caseLayout.ts";
import { SectionLabel } from "./general/SectionLabel";
import { useGuide } from "./guide/GuideContext.tsx";

/** How strongly the open section is washed with the primary colour. */
const OPEN_SECTION_ALPHA = 0.1;

/**
 * What a section still lacks. Something `blocking` stops the calculation
 * outright; a `warning` or an `info` leaves it running.
 */
type Mark = "blocking" | "warning";

type Section = { path: string; icon: ReactNode; mark?: Mark };

const SectionLink = ({
  label,
  to,
  icon,
  mark,
  isRail,
}: {
  label: string;
  to: string;
  icon: ReactNode;
  mark?: Mark;
  isRail: boolean;
}) => {
  const { theme } = useTheme();

  // "!" rather than a number: a numeral next to Claims reads as a count of
  // claims, not of unmet prerequisites — three missing things on an empty
  // claims section came out as "3"
  const markedIcon =
    mark === "blocking" ? (
      <Badge badgeContent="!" color="error">
        {icon}
      </Badge>
    ) : mark ? (
      <Badge variant="dot" color={mark}>
        {icon}
      </Badge>
    ) : (
      icon
    );

  return (
    <ListItemButton
      component={NavLink}
      end
      to={to}
      title={label}
      sx={{
        flexGrow: 0,
        minHeight: 38,
        gap: 1.375,
        // a rail is too narrow for padding beside the icon it centres
        px: isRail ? 0 : 1.25,
        py: 0,
        borderRadius: 1.75,
        justifyContent: isRail ? "center" : "flex-start",
        typography: "body2",
        fontWeight: 500,
        color: "text.primary",
        "& .MuiSvgIcon-root": { fontSize: 20, color: "text.secondary" },
        // NavLink marks the link of the section that is open
        "&.active": {
          bgcolor: alpha(theme.palette.primary.main, OPEN_SECTION_ALPHA),
          color: "primary.main",
          fontWeight: 700,
          "& .MuiSvgIcon-root": { color: "inherit" },
        },
      }}
    >
      {/* a rail has no room beside the icon, so the mark sits on it */}
      {isRail ? markedIcon : icon}
      {!isRail && (
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
          {label}
        </Box>
      )}
      {!isRail && mark === "blocking" && (
        <Box
          component="span"
          sx={{
            flex: "none",
            width: 17,
            height: 17,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "error.main",
            color: "error.contrastText",
            typography: "caption",
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          !
        </Box>
      )}
      {!isRail && mark && mark !== "blocking" && (
        <Box
          component="span"
          sx={{
            flex: "none",
            width: 8,
            height: 8,
            mr: 0.5,
            borderRadius: "50%",
            bgcolor: `${mark}.main`,
          }}
        />
      )}
    </ListItemButton>
  );
};

/**
 * The sections of a case, beside its pages: the overview, what the user
 * configures and what is calculated from it.
 */
export const CaseNavigation = () => {
  const featureFlags = useFeatureFlags();
  const { active: persona } = usePersona();
  const caseContext = useCase();
  const c = caseContext.getCase();
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "case.details" });
  // the open guide folds the navigation where the page would be squeezed
  const foldedByGuide = useGuide()?.foldsNavigation ?? false;
  const isRail = useMediaQuery(theme.breakpoints.down("md")) || foldedByGuide;
  const width = isRail ? RAIL_WIDTH : NAVIGATION_WIDTH;

  // Same descriptor the analytics checklist renders from, so the navigation
  // cannot point somewhere the checklist doesn't. Anything unmet here blocks
  // the calculation outright; gaps that don't block it get a warning dot — a
  // missing fee structure leaves the firm's figures unknown, and a party
  // without costs matters only once another party has some.
  const unmet = new Set(
    caseContext
      .getPrerequisites()
      .filter(({ met }) => !met)
      .map(({ id }) => id),
  );
  const blocking = (...ids: PrerequisiteId[]): Mark | undefined =>
    ids.some((id) => unmet.has(id)) ? "blocking" : undefined;
  const feeStructureMissing = !feeStructurePrerequisite(c).met;
  // totals every budget, so once per loaded case rather than per render
  const partyCostsMissing = useMemo(
    () => partiesWithoutCosts(c).length > 0,
    [c],
  );

  // the law firm's own figures, which an insurer has no use for
  const firmOnly = new Set(
    persona === "lawFirm" ? [] : ["costStructure", "revenue"],
  );
  const configuration: Section[] = [
    { path: "parties", icon: <PartiesIcon />, mark: blocking("parties") },
    { path: "timeline", icon: <TimelineIcon /> },
    {
      // claims carry three of the five: the claim itself, its amounts and its
      // win chances
      path: "claims",
      icon: <ClaimsIcon />,
      mark: blocking("claims", "amounts", "winChances"),
    },
    {
      // no costs at all blocks; some parties without costs only warns, and the
      // tabs on the costs page say which
      path: "costs",
      icon: <CostsIcon />,
      mark: blocking("costs") ?? (partyCostsMissing ? "warning" : undefined),
    },
    {
      path: "costStructure",
      icon: <FeeStructureIcon />,
      mark: feeStructureMissing ? "warning" : undefined,
    },
    { path: "agreements", icon: <AgreementsIcon /> },
    { path: "recoveries", icon: <RecoveriesIcon /> },
    { path: "apportionments", icon: <ApportionmentsIcon /> },
    { path: "settlement", icon: <SettlementIcon /> },
  ];
  const analytics: Section[] = [
    // what a claim costs an insurer, first among what it reads
    ...(persona === "insurer"
      ? [{ path: "reserve", icon: <ReserveIcon /> }]
      : []),
    { path: "outcomes", icon: <OutcomesIcon /> },
    { path: "netResults", icon: <NetResultIcon /> },
    { path: "risk", icon: <RiskIcon /> },
    { path: "value", icon: <ValueIcon /> },
    // { path: "sensitivity", icon: <SensitivityIcon /> },
    { path: "revenue", icon: <RevenueIcon /> },
    { path: "bargaining", icon: <BargainingIcon /> },
    // { path: "var", icon: <VarIcon /> },
  ];
  if (featureFlags?.decisionTree) {
    analytics.push({ path: "tree", icon: <TreeIcon /> });
  }
  const shown = (sections: Section[]) =>
    sections.filter(({ path }) => !firmOnly.has(path));

  // a rail has no room for the name of a group, a line parts the groups there
  const group = (name: "configuration" | "analytics", sections: Section[]) => (
    <>
      {isRail ? (
        <Divider sx={{ my: 1 }} />
      ) : (
        <Box sx={{ px: 1.25, pt: 2, pb: 0.75 }}>
          <SectionLabel>{t(`label.${name}`)}</SectionLabel>
        </Box>
      )}
      {sections.map(({ path, icon, mark }) => (
        <SectionLink
          key={path}
          label={t(`section.${name}.${path}`, path)}
          to={`${name}/${path}`}
          icon={icon}
          mark={mark}
          isRail={isRail}
        />
      ))}
    </>
  );

  return (
    <Drawer
      variant="permanent"
      slotProps={{ paper: { elevation: 0 } }}
      sx={{
        flexShrink: 0,
        width,
        "& .MuiDrawer-paper": { width },
      }}
    >
      {/* the navigation starts under the app bar, which lies over it */}
      <Toolbar />
      <Box
        component="nav"
        aria-label={t("label.sections")}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
          px: 1.25,
          pt: 1.75,
          pb: 2.75,
        }}
      >
        <SectionLink
          label={t("section.overview")}
          to=""
          icon={<OverviewIcon />}
          isRail={isRail}
        />
        {group("configuration", shown(configuration))}
        {group("analytics", shown(analytics))}
      </Box>
    </Drawer>
  );
};
